import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Volume2, VolumeX, Play } from "lucide-react";
import posterFrame from "@/assets/demo-poster-frame1.png.asset.json";
import demoVideoEn from "@/assets/landing-demo-en.mp4.asset.json";
import demoVideoEs from "@/assets/landing-demo-es.mp4.asset.json";

/**
 * Landing-page product demo — native <video> player for all languages.
 *
 * Rules:
 *  - Muted by default. Audio plays ONLY when the user toggles sound or
 *    enters fullscreen.
 *  - Desktop/tablet: autoplays muted when ≥20% in view; pauses when <20%.
 *  - Mobile (<768px): does NOT autoplay. Shows poster + centered play
 *    button. Tapping starts playback and enters fullscreen (native iOS
 *    player on Safari). Exiting fullscreen resets to poster state.
 */

function pickVideo(lang: string) {
  if (lang === "es") return demoVideoEs;
  return demoVideoEn;
}

export function LandingProductDemo() {
  const { i18n, t } = useTranslation();
  const lang = (i18n.language || "es").toLowerCase().split("-")[0];
  const demoVideo = pickVideo(lang);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isMobile, setIsMobile] = useState(false);
  const [started, setStarted] = useState(false); // mobile poster gate
  const [muted, setMuted] = useState(true);

  // Detect mobile (< 768px)
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  // Visibility: pause/resume based on ≥20% in view (desktop/tablet only,
  // and on mobile only AFTER the user has started it).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const v = videoRef.current;
        if (!v) return;
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        if (visible) {
          if (isMobile && !started) return;
          if (v.paused) v.play().catch(() => {});
        } else {
          if (!v.paused) v.pause();
        }
      },
      { threshold: [0, 0.2, 0.5, 0.8] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isMobile, started]);

  // Keep video.muted in sync with React state
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = muted;
  }, [muted]);

  // Reset to poster state when user exits fullscreen (mobile)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const resetToPoster = () => {
      try {
        v.pause();
        v.currentTime = 0;
      } catch {}
      v.muted = true;
      setMuted(true);
      setStarted(false);
    };

    const onWebkitEnd = () => {
      if (isMobile) resetToPoster();
    };
    const onFsChange = () => {
      const fsEl =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        null;
      if (!fsEl && isMobile) resetToPoster();
    };

    v.addEventListener("webkitendfullscreen", onWebkitEnd as any);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange as any);
    return () => {
      v.removeEventListener("webkitendfullscreen", onWebkitEnd as any);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange as any);
    };
  }, [isMobile]);

  const enterFullscreen = useCallback(async () => {
    const v = videoRef.current as any;
    if (!v) return;
    setMuted(false);
    v.muted = false;
    try {
      if (typeof v.webkitEnterFullscreen === "function") {
        v.webkitEnterFullscreen();
      } else if (typeof v.requestFullscreen === "function") {
        await v.requestFullscreen();
      } else if (typeof v.webkitRequestFullscreen === "function") {
        await v.webkitRequestFullscreen();
      }
      try { await (screen.orientation as any)?.lock?.("landscape"); } catch {}
    } catch {}
    if (v.paused) v.play().catch(() => {});
  }, []);

  const toggleSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    setMuted(next);
    v.muted = next;
    if (!next && v.paused) v.play().catch(() => {});
  }, [muted]);

  const onMobilePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    setStarted(true);
    setMuted(false);
    v.muted = false;
    try {
      v.currentTime = 0;
    } catch {}
    try {
      if (typeof (v as any).webkitEnterFullscreen === "function") {
        (v as any).webkitEnterFullscreen();
      } else if (typeof v.requestFullscreen === "function") {
        await v.requestFullscreen();
      }
    } catch {}
    v.play().catch(() => {});
  };

  return (
    <section className="relative bg-white py-10 md:py-14 lg:py-20">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="relative mx-auto w-full max-w-[1100px]">
          <div className="pointer-events-none absolute -inset-4 rounded-3xl bg-gradient-to-r from-[#61e5dc]/20 via-[#61e5dc]/10 to-[#61e5dc]/20 blur-2xl hidden md:block" />

          <div
            ref={wrapperRef}
            className="group relative aspect-video w-full overflow-hidden rounded-[20px] border border-slate-200 bg-slate-100 shadow-lg md:shadow-2xl"
          >
            <video
              ref={videoRef}
              key={demoVideo.url}
              src={demoVideo.url}
              poster={posterFrame.url}
              className="absolute inset-0 h-full w-full object-cover"
              playsInline
              {...({ "webkit-playsinline": "true", "x5-playsinline": "true" } as any)}
              muted
              loop
              preload="metadata"
              controls={false}
            />

            {/* Mobile poster overlay until user taps play (re-appears on FS exit) */}
            {isMobile && !started && (
              <button
                type="button"
                onClick={onMobilePlay}
                aria-label={t("landingDemo.posterCta", "Play demo")}
                className="absolute inset-0 z-10 block bg-black"
              >
                <img
                  src={posterFrame.url}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  draggable={false}
                />
                <span className="absolute inset-0 bg-black/20" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <span className="relative inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-2xl ring-1 ring-black/10">
                    <span className="absolute inset-0 animate-ping rounded-full bg-white/50" />
                    <Play className="relative h-9 w-9 translate-x-[2px] fill-current" />
                  </span>
                </span>
              </button>
            )}

            {/* Controls overlay (desktop/tablet; mobile uses native player in FS) */}
            {!isMobile && (
              <>
                <button
                  type="button"
                  onClick={toggleSound}
                  aria-label={muted ? "Unmute" : "Mute"}
                  className="absolute bottom-3 right-16 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
                >
                  {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <button
                  type="button"
                  onClick={enterFullscreen}
                  aria-label="Fullscreen"
                  className="absolute bottom-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
                >
                  <Maximize2 className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingProductDemo;
